import { UserEnumerations } from '~/lib/constants/url/enumerations';
import urlConstants from '~/lib/constants/url.constants';
import { sortAndMapEnumerationToLabel } from '~/lib/util/enumerations';

import api from './api';
import {
  TreatmentAttributeEnum,
  TreatmentLevelEnumeration,
  TreatmentModalityEnumeration,
} from '~/lib/types/treatmentAttributes/treatmentAttributes';
import { EpisodeTreatmentProgram } from '~/lib/constants/patients.constants';

export const ENUMERATION_CACHE_TIME = 500 * 60;

export const enumerationApi = api.injectEndpoints({
  endpoints: (build) => ({
    getAdminEpisodeStatusReasonsEnum: build.query<EpisodeStatusReason[], void>({
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
      query: () => urlConstants.clinical_data.getAdminEpisodeStatusReasons,
      transformResponse: (response: EpisodeStatusReason[]) =>
        response.toSorted((e1, e2) => e1.label.localeCompare(e2.label)),
    }),
    getAdminNoteActivityWithNamesEnum: build.query<
      { activityWithName: string; code: string }[],
      void
    >({
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
      query: () => urlConstants.clinical_data.getActivityWith,
      transformResponse: (
        response: {
          activityWithName: string;
          code: string;
          noteTypeExternalId: string;
        }[],
      ) =>
        response
          .map((item) => ({
            activityWithName: item.activityWithName,
            code: item.code,
          }))
          .sort((a, b) => a.activityWithName.localeCompare(b.activityWithName)),
    }),
    getAdminNoteTypesEnum: build.query<string[], void>({
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
      query: () => urlConstants.clinical_data.getNoteTypes,
      transformResponse: (
        response: {
          isActive: boolean;
          noteType: string;
          noteTypeExternalId: string;
        }[],
      ) =>
        response
          .filter((noteType) => noteType.isActive)
          .map((noteType) => noteType.noteType)
          .sort((a, b) => a.localeCompare(b)),
    }),
    getPatientDocumentTags: build.query<
      { value: string; label: string }[],
      void
    >({
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
      query: () => ({
        url: urlConstants.clinical_data.getPatientDocumentTags,
      }),
      transformResponse: (response: { code: string; label: string }[]) =>
        response.map(({ code, label }) => ({ label, value: code })),
    }),
    getContactPreferencesEnum: build.query<string[], void>({
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
      query: () => UserEnumerations.CONTACT_PREFERENCES,
      transformResponse: (response: Enumeration[]) =>
        response.map(({ label }) => label).sort((a, b) => a.localeCompare(b)),
    }),
    getCoverageSubscriberRelationsToPatient: build.query<
      { label: string; externalId: string }[],
      void
    >({
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
      query: () => urlConstants.clinical_data.patientRelationsToSubscriber,
    }),
    getDiagnosisStatusesEnum: build.query<DiagnosisStatuses[], void>({
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
      query: () => urlConstants.clinical_data.getDiagnosisStatuses,
    }),
    getDiagnosisVoidReasonsEnum: build.query<DiagnosisVoidReason[], void>({
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
      query: () => urlConstants.clinical_data.getDiagnosisStatusReasons,
    }),
    getTreatmentPrograms: build.query<Enumeration[], void>({
      query: () => urlConstants.clinical_data.getTreatmentPrograms,
    }),
    getTreatmentProgramsEnum: build.query<
      EpisodeTreatmentProgram[],
      string[] | undefined
    >({
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
      query: () => urlConstants.clinical_data.getTreatmentPrograms,
      transformResponse: (
        response: Enumeration[],
        _meta,
        programExclusions,
      ) => {
        const excluded = new Set(programExclusions ?? []);
        return response
          .filter(({ label }) => !excluded.has(label))
          .map(({ label }) => label as EpisodeTreatmentProgram)
          .toSorted((a, b) => a.localeCompare(b));
      },
    }),
    getEthnicitiesEnum: build.query<Enumeration[], void>({
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
      query: () => UserEnumerations.ETHNICITIES,
      transformResponse: (response: Enumeration[]) =>
        response.toSorted((e1, e2) => e1.label.localeCompare(e2.label)),
    }),
    getGendersEnum: build.query<Enumeration[], void>({
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
      query: () => UserEnumerations.GENDERS,
      transformResponse: (response: Enumeration[]) =>
        response.toSorted((e1, e2) => e1.label.localeCompare(e2.label)),
    }),
    getInsuranceVerificationStatusesEnum: build.query<
      { label: string; externalId: string }[],
      void
    >({
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
      query: () => urlConstants.clinical_data.getInsuranceVerificationStatuses,
    }),
    getMedicalClearanceStatusesEnum: build.query<
      {
        label: string;
        externalId: string;
      }[],
      void
    >({
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
      query: () => urlConstants.clinical_data.getMedicalClearanceStatuses,
    }),

    getNoteCommunicationMethodsEnum: build.query<string[], void>({
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
      query: () => urlConstants.clinical_data.getNoteCommunicationMethods,
      transformResponse: (
        response: {
          methodName: string;
          communicationMethodExternalId: string;
        }[],
      ) =>
        response
          .map(({ methodName }) => methodName)
          .sort((a, b) => a.localeCompare(b)),
    }),
    getNoteVoidReasonsEnum: build.query<NoteVoidReason[], void>({
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
      query: () => urlConstants.clinical_data.listVoidReasons,
    }),
    getObservationGoalFrequenciesEnum: build.query<GoalFrequency[], void>({
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
      query: () => urlConstants.clinical_data.getObservationGoalFrequencies,
    }),
    getObservationGoalMethodsEnum: build.query<GoalMethod[], void>({
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
      query: () => urlConstants.clinical_data.getObservationGoalMethods,
    }),
    getObservationGoalStatusesEnum: build.query<GoalStatus[], void>({
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
      query: () => urlConstants.clinical_data.getObservationGoalStatuses,
    }),
    getObservationGoalTypesEnum: build.query<GoalType[], void>({
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
      query: () => urlConstants.clinical_data.getObservationGoalTypes,
    }),
    getObservationInterpretationsEnum: build.query<
      PatientVitalsObservationInterpretation[],
      void
    >({
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
      query: () => urlConstants.clinical_data.getObservationInterpretations,
    }),
    getObservationVoidReasonsEnum: build.query<
      PatientVitalsObservationVoidReason[],
      void
    >({
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
      query: () => urlConstants.clinical_data.getObservationVoidReasons,
    }),
    getPatientDocumentCategoriesEnum: build.query<
      PatientDocumentCategory[],
      void
    >({
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
      query: () => urlConstants.clinical_data.getPatientDocumentCategories,
    }),
    getPatientTagsEnum: build.query<Enumeration[], void>({
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
      query: () => UserEnumerations.TAGS,
    }),
    getPayorProgramsEnum: build.query<PayorProgram[], void>({
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
      query: () => urlConstants.clinical_data.getAllPayorPrograms,
    }),
    getPayorsEnum: build.query<Payor[], void>({
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
      query: () => urlConstants.clinical_data.getPayors,
    }),
    getPronounsEnum: build.query<string[], void>({
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
      query: () => UserEnumerations.PRONOUNS,
      transformResponse: (response: Enumeration[]) =>
        sortAndMapEnumerationToLabel(response ?? []),
    }),
    getProviderCapacityEnum: build.query<Enumeration[], void>({
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
      query: () => UserEnumerations.CAPACITY,
    }),
    getProviderCredentialsEnum: build.query<Enumeration[], void>({
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
      query: () => UserEnumerations.CREDENTIALS,
    }),
    getProviderLanguagesEnum: build.query<Enumeration[], void>({
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
      query: () => UserEnumerations.LANGUAGES,
    }),
    getProviderPronounsEnum: build.query<Enumeration[], void>({
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
      query: () => UserEnumerations.PRONOUNS,
    }),
    getProviderStatesEnum: build.query<
      { label: string; externalId: string }[],
      void
    >({
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
      query: () => UserEnumerations.STATES,
    }),
    getProviderTimezonesEnum: build.query<Enumeration[], void>({
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
      query: () => UserEnumerations.TIMEZONES,
    }),
    getProviderTypesEnum: build.query<Enumeration[], void>({
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
      query: () => UserEnumerations.PROVIDER_TYPES,
    }),
    getPsychClearanceStatusesEnum: build.query<
      {
        label: string;
        externalId: string;
      }[],
      void
    >({
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
      query: () => urlConstants.clinical_data.getPsychClearanceStatuses,
    }),
    getRegionsEnum: build.query<string[], void>({
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
      query: () => UserEnumerations.REGIONS,
      transformResponse: (response: Enumeration[]) =>
        sortAndMapEnumerationToLabel(response ?? []),
    }),
    getRelationsToPatientEnum: build.query<
      RelationToPatientEnumeration[],
      void
    >({
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
      query: () => UserEnumerations.RELATIONS_TO_PATIENT,
      transformResponse: (response: Enumeration[]) =>
        response.toSorted((e1, e2) => e1.label.localeCompare(e2.label)),
    }),
    getSexesEnum: build.query<string[], void>({
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
      query: () => UserEnumerations.SEXES,
      transformResponse: (response: Enumeration[]) =>
        sortAndMapEnumerationToLabel(response),
    }),
    getTaskVoidReasonsEnum: build.query<VoidReason[], void>({
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
      query: () => urlConstants.tasks.listVoidReasons,
    }),
    getTreatmentEpisodeLevelsEnum: build.query<
      TreatmentLevelEnumeration[],
      void
    >({
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
      query: () => urlConstants.clinical_data.getEpisodeLevels,
    }),
    getTreatmentEpisodeStatusesEnum: build.query<
      TreatmentEpisodeStatusEnum[],
      void
    >({
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
      query: () => urlConstants.clinical_data.getTreatmentEpisodeStatuses,
    }),
    getTreatmentModalitiesEnum: build.query<
      TreatmentModalityEnumeration[],
      void
    >({
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
      query: () => urlConstants.treatmentAttributes.getTreatmentModalities,
    }),
    getGroupTypesEnum: build.query<GroupTypeResponse[], void>({
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
      query: () => urlConstants.groupClasses.groupTypes,
    }),
    getTreatmentDiscussionsOfWeightEnum: build.query<
      TreatmentAttributeEnum[],
      void
    >({
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
      query: () =>
        urlConstants.treatmentAttributes.getTreatmentDiscussionsOfWeight,
    }),
    getTreatmentSaferStagesEnum: build.query<TreatmentAttributeEnum[], void>({
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
      query: () => urlConstants.treatmentAttributes.getSaferStages,
    }),
    getTreatmentRefeedingRisksEnum: build.query<TreatmentAttributeEnum[], void>(
      {
        keepUnusedDataFor: ENUMERATION_CACHE_TIME,
        query: () => urlConstants.treatmentAttributes.getRefeedingRisks,
      },
    ),
    getTreatmentMedicalStabilitiesEnum: build.query<
      TreatmentAttributeEnum[],
      void
    >({
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
      query: () => urlConstants.treatmentAttributes.getMedicalStabilities,
    }),
    getTreatmentPsychiatricStabilitiesEnum: build.query<
      TreatmentAttributeEnum[],
      void
    >({
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
      query: () => urlConstants.treatmentAttributes.getPsychiatricStabilities,
    }),
    getTreatmentEpisodeStatusReasonsEnum: build.query<
      EpisodeStatusReason[],
      void
    >({
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
      query: () => urlConstants.clinical_data.getTreatmentEpisodeStatusReasons,
    }),
    getPatientDocumentVoidReasons: build.query<
      { label: string; value: string }[],
      void
    >({
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
      query: () => urlConstants.clinical_data.getPatientDocumentVoidReasons,
      transformResponse: (
        response: {
          label: string;
          code: string;
        }[],
      ) =>
        response.map(({ label, code }) => ({
          label,
          value: code,
        })),
    }),
    getUniversalStatusesEnum: build.query<UniversalStatus[], void>({
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
      query: () => urlConstants.users.getUniversalStatuses,
    }),
  }),
});

export const {
  useGetAdminEpisodeStatusReasonsEnumQuery,
  useGetContactPreferencesEnumQuery,
  useGetAdminNoteActivityWithNamesEnumQuery,
  useGetAdminNoteTypesEnumQuery,
  useGetDiagnosisStatusesEnumQuery,
  useGetDiagnosisVoidReasonsEnumQuery,
  useGetTreatmentProgramsEnumQuery,
  useGetTreatmentProgramsQuery,
  useGetGendersEnumQuery,
  useGetInsuranceVerificationStatusesEnumQuery,
  useGetMedicalClearanceStatusesEnumQuery,
  useGetNoteCommunicationMethodsEnumQuery,
  useGetObservationGoalFrequenciesEnumQuery,
  useGetObservationGoalMethodsEnumQuery,
  useGetObservationGoalTypesEnumQuery,
  useGetObservationGoalStatusesEnumQuery,
  useGetObservationInterpretationsEnumQuery,
  useGetObservationVoidReasonsEnumQuery,
  useGetPatientDocumentCategoriesEnumQuery,
  useGetPatientDocumentVoidReasonsQuery,
  useGetPatientTagsEnumQuery,
  useGetPayorProgramsEnumQuery,
  useGetPronounsEnumQuery,
  useGetProviderCredentialsEnumQuery,
  useGetProviderLanguagesEnumQuery,
  useGetProviderPronounsEnumQuery,
  useGetProviderTypesEnumQuery,
  useGetProviderStatesEnumQuery,
  useGetProviderTimezonesEnumQuery,
  useGetProviderCapacityEnumQuery,
  useGetTaskVoidReasonsEnumQuery,
  useGetSexesEnumQuery,
  useGetEthnicitiesEnumQuery,
  useGetRelationsToPatientEnumQuery,
  useGetTreatmentEpisodeStatusesEnumQuery,
  useGetTreatmentEpisodeStatusReasonsEnumQuery,
  useGetUniversalStatusesEnumQuery,
  useGetCoverageSubscriberRelationsToPatientQuery,
  useGetPayorsEnumQuery,
  useGetNoteVoidReasonsEnumQuery,
  useGetPsychClearanceStatusesEnumQuery,
  useGetTreatmentEpisodeLevelsEnumQuery,
  useGetTreatmentModalitiesEnumQuery,
  useGetTreatmentDiscussionsOfWeightEnumQuery,
  useGetTreatmentSaferStagesEnumQuery,
  useGetPatientDocumentTagsQuery,
  useGetGroupTypesEnumQuery,
} = enumerationApi;
